import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, Typography, Button, Box, TextField, Divider, CircularProgress } from '@mui/material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import BankingService from 'services/banking.service';
import { IBankSlip } from 'common/bank-slip.interface';
import { EPixOperationType, IBankSlipTransactionPayload } from 'types/banking';
import { useSnackbar } from 'notistack';
import SuccessModal from './SuccessModal';

interface BoletoPaymentModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type BoletoInfo = Partial<IBankSlip>;

const formatDueDate = (value?: string) => {
  if (!value) return '-';
  const datePart = value.split('T')[0];
  const parts = datePart.split('-');
  if (parts.length !== 3) return value;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const formatCurrency = (value?: number) =>
  value != null
    ? value.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    : '-';

const boletoValidationSchema = Yup.object({
  barCode: Yup.string().min(12, 'Código deve ter pelo menos 12 dígitos').required('Código de barras é obrigatório'),
  dataPagamento: Yup.mixed().required('Data de pagamento é obrigatória'),
  descricao: Yup.string(),
});

const bankingService = new BankingService();

function useBoletoSearch() {
  const [boletoInfo, setBoletoInfo] = useState<BoletoInfo | null>(null);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detectedFormat, setDetectedFormat] = useState<'barcode' | 'digitable_line' | null>(null);

  const validate = useCallback(async (code: string) => {
    const cleaned = code.replace(/\D/g, '');
    if (cleaned.length < 12) {
      setBoletoInfo(null);
      setError(null);
      setDetectedFormat(null);
      return;
    }

    setValidating(true);
    setError(null);

    try {
      // Detect format
      let format: 'barcode' | 'digitable_line';

      if (cleaned.length === 47) {
        format = 'digitable_line';
      } else if (cleaned.length === 44 || (cleaned.length >= 12 && cleaned.length <= 13)) {
        format = 'barcode';
      } else {
        format = 'digitable_line';
      }

      setDetectedFormat(format);

      const result = await bankingService.searchBankSlip(cleaned);
      setBoletoInfo(result);
    } catch (err: any) {
      setError(err?.data?.detail || 'Erro ao buscar informações do boleto');
      setBoletoInfo(null);
    } finally {
      setValidating(false);
    }
  }, []);

  return {
    boletoInfo,
    validating,
    error,
    detectedFormat,
    validate,
    clear: () => {
      setBoletoInfo(null);
      setError(null);
      setDetectedFormat(null);
    },
  };
}

interface InfoRowProps {
  label: string;
  value?: React.ReactNode;
  bold?: boolean;
}

const InfoRow = ({ label, value = '-', bold }: InfoRowProps) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={bold ? 600 : undefined}>
      {value}
    </Typography>
  </Box>
);

interface BoletoInfoSectionProps {
  validating: boolean;
  boletoInfo: BoletoInfo | null;
}

const BoletoInfoSection = ({ validating, boletoInfo }: BoletoInfoSectionProps) => (
  <Box>
    <Typography variant="h6" sx={{ textAlign: 'center', mb: 2, fontWeight: 600 }}>
      Informações do boleto
    </Typography>

    {validating ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <InfoRow label="Favorecido" value={boletoInfo?.recipient} />
        <InfoRow label="Devedor original" value={boletoInfo?.originalDebtor} />
        <InfoRow label="Vencimento do boleto" value={formatDueDate(boletoInfo?.dueDate)} />

        <Divider sx={{ my: 1 }} />

        <InfoRow label="Valor do boleto" value={formatCurrency(boletoInfo?.originalValue)} bold />
        <InfoRow label="Encargos" value={formatCurrency(boletoInfo?.fineValue)} />
        <InfoRow label="Juros/Mora" value={formatCurrency(boletoInfo?.interestValue)} />
        <InfoRow label="Descontos" value={formatCurrency(boletoInfo?.discountValue)} />
        <InfoRow label="Valor do pagamento" value={formatCurrency(boletoInfo?.totalValue)} bold />
      </Box>
    )}
  </Box>
);

const BoletoPaymentModal = ({ open, onClose, onSuccess }: BoletoPaymentModalProps) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { boletoInfo, validating, error, detectedFormat, validate, clear } = useBoletoSearch();
  const { enqueueSnackbar } = useSnackbar();
  const bankingService = new BankingService();

  const formik = useFormik({
    initialValues: {
      barCode: '',
      dataPagamento: new Date().toISOString().split('T')[0],
      descricao: '',
    },
    validationSchema: boletoValidationSchema,
    onSubmit: async (values) => {
      try {
        setIsLoading(true);

        // Remove all non-digit characters from barCode before sending
        const sanitizedBarCode = values.barCode.replace(/\D/g, '');

        const payload: IBankSlipTransactionPayload = {
          barCode: sanitizedBarCode,
          transferDate: values.dataPagamento || undefined,
          description: values.descricao || undefined,
          value: boletoInfo?.totalValue ?? 0,
        };

        await bankingService.createTransaction(EPixOperationType.BANK_SLIP, payload);

        enqueueSnackbar('Pagamento de boleto criado com sucesso.', { variant: 'success' });
        setShowSuccess(true);
        onSuccess?.();
      } catch (err: any) {
        const errorMessage = err?.response?.data?.detail || 'Erro ao criar pagamento de boleto. Tente novamente.';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (formik.values.barCode) {
        validate(formik.values.barCode);
      }
    }, 500);

    return () => clearTimeout(debounce);
  }, [formik.values.barCode, validate]);

  const handleClose = useCallback(() => {
    formik.resetForm();
    clear();
    onClose();
  }, [formik, clear, onClose]);

  const handleSuccessClose = useCallback(() => {
    setShowSuccess(false);
    handleClose();
  }, [handleClose]);

  const handleDialogClose: React.ComponentProps<typeof Dialog>['onClose'] = (event, reason) => {
    if (isLoading && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    handleClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={isLoading}
        PaperProps={{ sx: { borderRadius: 3, p: 2 } }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            <QrCode2Icon sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700}>
              Pagamento de Boleto
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Código de barras ou linha digitável*"
                variant="standard"
                name="barCode"
                placeholder="Digite ou copie e cole o código"
                value={formik.values.barCode}
                onChange={formik.handleChange}
                disabled={isLoading}
                error={formik.touched.barCode && Boolean(formik.errors.barCode)}
                helperText={formik.touched.barCode && formik.errors.barCode}
                InputLabelProps={{ shrink: true, sx: { fontSize: '16px', fontWeight: 500 } }}
                InputProps={{ sx: { fontSize: '14px' } }}
              />

              {error && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {error}
                </Typography>
              )}

              {detectedFormat && !error && (
                <Typography variant="caption" color="success.main" sx={{ mt: 1 }}>
                  {detectedFormat === 'barcode' ? '✓ Código de barras detectado' : '✓ Linha digitável detectada'}
                </Typography>
              )}

              <Divider />

              <BoletoInfoSection validating={validating} boletoInfo={boletoInfo} />

              <Divider />

              <TextField
                fullWidth
                variant="standard"
                label="Data para pagamento *"
                name="dataPagamento"
                type="date"
                value={formik.values.dataPagamento}
                onChange={formik.handleChange}
                disabled={isLoading}
                error={formik.touched.dataPagamento && Boolean(formik.errors.dataPagamento)}
                helperText={formik.touched.dataPagamento && (formik.errors.dataPagamento as string)}
                InputLabelProps={{
                  shrink: true,
                  sx: { fontSize: '16px', fontWeight: 500 },
                }}
                inputProps={{ min: new Date().toISOString().split('T')[0], sx: { fontSize: '14px' } }}
              />

              <TextField
                fullWidth
                variant="standard"
                label="Descrição (opcional)"
                name="descricao"
                placeholder="Pagamento de fornecedor"
                value={formik.values.descricao}
                onChange={formik.handleChange}
                disabled={isLoading}
                InputLabelProps={{ shrink: true, sx: { fontSize: '16px', fontWeight: 500 } }}
                InputProps={{ sx: { fontSize: '14px' } }}
              />

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button variant="outlined" onClick={handleClose} disabled={isLoading} fullWidth sx={{ borderRadius: 6, py: 1.5 }}>
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  fullWidth
                  disabled={!formik.isValid || !formik.dirty || isLoading}
                  sx={{ borderRadius: 6, py: 1.5 }}
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Confirmar'}
                </Button>
              </Box>
            </Box>
          </form>
        </DialogContent>
      </Dialog>

      <SuccessModal open={showSuccess} onClose={handleSuccessClose} />
    </>
  );
};

export default BoletoPaymentModal;
